import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	CircularProgress,
	FormLabel,
	Container,
	Grid,
	useTheme,
} from '@mui/material';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import { Form, Formik, useFormik, useFormikContext } from 'formik';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { currentYear, nextYear } from '@/utils/date';
import { validationSchema } from './validationSchema';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { PolicySelect } from '@/components/selects/PolicySelect';
import { PageHeader } from '@/components/layouts/PageHeader';
import { showToast } from '@/features/toast/toastSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormikErrorScroll, useFormikHelper } from '@/hooks/useFormikHelper';
import { reset } from '@/features/select/clientSelectSlice';
import { paths } from '@/app/routes';
import { getPipelineIcon, getPipelineName, getUrlFromPipeline } from '@/utils/constants';
import { InstantDealSelect } from '@/components/selects/DealSelect';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import { useCreateNoteMutation } from '@/features/engagements/engagementsApi';
import { Tooltip } from '@mui/material';
import { IS_DEV } from '@/utils/environment';
import { usePipelineContext } from '@/context/pipelineContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import FormNavigationBlocker from '../FormNavigationBlocker';
import { usePoliciesSelect } from '@/context/policiesContext';
import { uniqueId } from 'lodash';
import DealFormMessages from '@/components/forms/CreateFormFields/DealFormMessages';
import { useSyncBrokerGroupFromClient } from '@/hooks/useSyncBrokerGroupFromClient';
import { useLocationStatePolicies } from '@/hooks/useLocationStatePolicies';
import NotesField from '@/components/forms/CreateFormFields/NotesField';
import FileUploadField from '@/components/forms/CreateFormFields/FileUploadField';
import ClientSearchField from './ClientSearchField';

const gridProps = {
	rowSpacing: 1,
	columnSpacing: 2,
};

export const CreateEndorsementStageForm = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { clearSelectedPolicies } = usePoliciesSelect();

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewEndorsements = Boolean(getFeatureFlags('endorsements')?.active);

	const existingClient = useMemo(() => location?.state?.client ?? {}, [location?.state?.client]);
	const existingDeal = useMemo(() => location?.state?.deal ?? {}, [location?.state?.deal]);
	const existingClientId = useMemo(() => existingClient?.clientId ?? '', [existingClient]);

	const { existingPolicy } = useLocationStatePolicies(existingClientId);

	const [formLoading, setFormLoading] = useState(false);

	const [createNote] = useCreateNoteMutation();
	const [updateDealRow] = useUpdateDealRowMutation();

	const [pipeline, setPipeline] = useState();
	const [brokerGroupId, setBrokerGroupId] = useState(userDetails?.brokerGroupId);

	const initialValues = useMemo(() => {
		const values = {
			endorsementsFeature: showNewEndorsements,
			policy: existingPolicy,
			policyId: existingPolicy?.Id ?? null,
			// Required by the shared endorsement validationSchema's folder rules.
			folderName: existingDeal?.dealName ?? `${currentYear}-${nextYear}`,
			deal: existingDeal ?? {},
			files: [],
			notes: '',
			clientName: existingClient?.name ?? '',
			successMessage: '',
			errorMessage: '',
			clientId: existingClientId,
		};
		return values;
	}, [existingPolicy, existingClient, existingClientId]);

	const { stages } = usePipelineContext(pipeline);

	const updateDealStage = async ({ deal }) => {
		try {
			const dealRowUpdateResponse = await updateDealRow({
				dealId: deal?.dealId,
				docId: deal?.id,
				properties: {
					dealStage: lastStage?.id,
				},
			}).unwrap();

			console.log('updated deal response: ', dealRowUpdateResponse);
			return dealRowUpdateResponse;
		} catch (error) {
			console.error('Error updating deal stage:', error);
			throw error;
		}
	};

	const lastStage = useMemo(
		() => (pipeline && stages.length > 0 ? stages[stages.length - 1] : null),
		[pipeline, stages],
	);

	const { uploadDealFilesToSharepoint } = useClientStorageHooks(brokerGroupId);

	return (
		<Formik
			{...{
				initialValues,
				validateOnChange: false,
				validationSchema,
				enableReinitialize: true,
				onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
					setFormLoading(true);
					setSubmitting(true);

					const toastId = uniqueId();
					dispatch(
						showToast({
							toastId,
							message: 'Creating endorsement...',
							action: {
								path: `${paths.endorsements}`,
								label: 'GO BACK TO ALL ENDORSEMENTS',
							},
							autohide: false,
						}),
					);

					try {
						const { deal, clientId, notes, files } = values;
						const objectType =
							deal?.objectType ??
							(IS_DEV && deal?.pipelineFolder === 'Endorsements' ? 'ticket' : 'deal');
						const noteBody = {
							clientId,
							dealId: deal?.dealId,
							noteBody: notes,
							objectType,
							subject: 'Endorsement Note',
						};

						if (notes.length > 0) {
							const noteRes = await createNote(noteBody).unwrap();
							await uploadDealFilesToSharepoint({
								dealId: deal?.dealId,
								noteId: noteRes?.noteId,
								driveId: deal?.driveId,
								pipelineId: deal?.pipeline,
								files,
							});
						}

						await updateDealStage({ deal });

						const path = getUrlFromPipeline(deal?.pipeline);
						const dealLink = `${path}/${deal?.dealId}`;

						dispatch(
							showToast({
								toastId,
								message: 'Endorsement in progress!',
								action: {
									path: dealLink,
									label: 'GO TO DEAL',
								},
								autohide: false,
							}),
						);

						const isOnCreateEndorsement =
							window.location.pathname.includes(paths.createEndorsement);

						if (isOnCreateEndorsement) {
							clearSelectedPolicies();
							resetForm();
							dispatch(reset());
							setFieldValue('successMessage', 'Successfully added Endorsement!');
							setFieldValue('errorMessage', '');
						}
					} catch (error) {
						console.error('Error:', error.message);

						const isOnCreateEndorsement =
							window.location.pathname.includes(paths.createEndorsement);
						if (isOnCreateEndorsement) {
							setFieldValue(
								'errorMessage',
								error.message ?? 'Error creating endorsement',
							);
							setFieldValue('successMessage', '');
						}
					} finally {
						const isOnCreateEndorsement =
							window.location.pathname.includes(paths.createEndorsement);
						if (isOnCreateEndorsement) {
							setSubmitting(false);
							setFormLoading(false);
						}
					}
				},
			}}
		>
			{({ isSubmitting }) => (
				<CreateEndorsementStageFormBody
					loading={formLoading || isSubmitting}
					setPipeline={setPipeline}
					setBrokerGroupId={setBrokerGroupId}
				/>
			)}
		</Formik>
	);
};

const CreateEndorsementStageFormBody = ({ loading, setPipeline, setBrokerGroupId }) => {
	const theme = useTheme();
	const { values, isSubmitting, setFieldValue, submitForm } = useFormikContext();
	const { getError, getErrorMessage } = useFormikHelper();

	const location = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { isLoading, isUninitialized } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const existingClient = useMemo(() => location?.state?.client ?? {}, [location?.state?.client]);
	const existingDeal = useMemo(() => location?.state?.deal ?? {}, [location?.state?.deal]);
	const { existingPolicy } = useLocationStatePolicies(values.clientId);

	const isExistingClient = useMemo(
		() => existingClient && Object.keys(existingClient).length > 0,
		[existingClient],
	);

	const { data: clientRow } = useGetClientRowQuery(values.clientId, {
		skip: !values.clientId,
	});

	useSyncBrokerGroupFromClient(clientRow, setBrokerGroupId);

	useEffect(() => {
		setPipeline(values.deal?.pipeline);
	}, [values.deal?.pipeline]);

	return (
		<Container sx={{ pt: 3 }}>
			<Form>
				<FormNavigationBlocker />
				<FormikErrorScroll isSubmitting={isSubmitting} />
				<PageHeader
					title={'Add Endorsement'}
					icon={getPipelineIcon(
						process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
						'40px',
						theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
					)}
				/>
				<CardContent>
					{isLoading || isUninitialized ? (
						<CircularProgress />
					) : (
						<Grid container {...gridProps}>
							<Grid size={12}>
								<ClientSearchField
									disabled={loading || isExistingClient}
									onClear={() => {
										setFieldValue('policy', {});
										setFieldValue('policyId', null);
									}}
								/>
							</Grid>
							<Grid size={12}>
								<FormLabel required>{'Associated deals'}</FormLabel>
								<InstantDealSelect
									displayClosed={true}
									disabled={
										isSubmitting ||
										!values.clientId ||
										Object.keys(existingDeal).length > 0
									}
									clientId={values.clientId}
									dealId={values.deal?.dealId}
									setDeal={(newDeal) => {
										if (newDeal) {
											setFieldValue('deal', newDeal);
											setFieldValue('folderName', newDeal?.dealName);
										} else {
											setFieldValue('deal', {});
										}
									}}
									autoPopulate={true}
								/>
								<FormHelperText error={getError('deal')}>
									{getErrorMessage('deal')}
								</FormHelperText>
							</Grid>
							<Grid size={12}>
								<FormLabel required sx={{ display: 'flex' }}>
									{'Policy'}
								</FormLabel>
								<PolicySelect
									clientId={values.clientId}
									policy={values.policy}
									setPolicy={(newPolicy) => {
										setFieldValue('policy', newPolicy);
										setFieldValue('policyId', newPolicy?.Id ?? -1);
									}}
									disabled={Object.keys(existingPolicy).length > 0 || loading}
								/>
								<FormHelperText error={getError('policyId')}>
									{getErrorMessage('policyId')}
								</FormHelperText>
							</Grid>
							<Grid size={12}>
								<NotesField loading={loading} />
							</Grid>
							<Grid size={12}>
								<FileUploadField loading={loading} />
							</Grid>
							<DealFormMessages />
						</Grid>
					)}
				</CardContent>
				<Divider />
				<CardActions sx={{ justifyContent: 'center', p: 2, gap: 2 }}>
					<Tooltip title='Save your progress without completing the endorsement' arrow>
						<span>
							<Button
								color='primary'
								// type='submit'
								variant='contained'
								onClick={(e) => {
									e.preventDefault();
									submitForm();
								}}
								disabled={isLoading || isUninitialized || loading}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Submit'
								)}
							</Button>
						</span>
					</Tooltip>
				</CardActions>
			</Form>
		</Container>
	);
};
